import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import handTapIconUrl from '@/assets/icons/hand-tap-fill.svg?url';
import settingsReferenceIconUrl from '@/assets/icons/summary-settings-reference.svg?url';
import amexLogoUrl from '@/assets/payment-brands/american-express.svg?url';
import discoverLogoUrl from '@/assets/payment-brands/discover.svg?url';
import mastercardLogoUrl from '@/assets/payment-brands/mastercard.svg?url';
import visaLogoUrl from '@/assets/payment-brands/visa.svg?url';
import { IosSettingsIcon } from '@/components/ui/IosReferenceIcons';
import { PaymentError } from '@/components/ui/PaymentError';
import { useWebTrialCheckout } from '@/hooks/useWebTrialCheckout';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { isValidEmailAddress } from '@/utils/email';
import {
  getStoredAttParams,
  resolveAttributionEmail,
} from '@/utils/attParams';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';

const TOTAL_DETECTED_ISSUES = 29;
const SCAN_DURATION_MS = 3800;
const RESULT_DELAY_MS = 420;
const STAGE_TRANSITION_MS = 360;
const CHECKOUT_REVEAL_MS = 520;
const IOS_SYSTEM_BLUE = '#007aff';
const IOS_SYSTEM_BLUE_DISABLED = 'rgba(0, 122, 255, 0.35)';
const IOS_SYSTEM_BLUE_FOCUS = 'rgba(0, 122, 255, 0.35)';
const IOS_SYSTEM_BLUE_SOFT = 'rgba(0, 122, 255, 0.10)';

type V8Stage = 'scan' | 'result' | 'checkout';

export interface PaywallV8Props {
  layout?: 'phone' | 'responsive';
  version?: 'v8' | 'v9';
}

interface IconProps {
  className?: string;
}

function WarningTriangleIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="none"
      focusable="false"
      aria-hidden="true"
    >
      <path
        d="M16 3.75 29.1 27.25H2.9L16 3.75Z"
        stroke="currentColor"
        strokeWidth="2.15"
        strokeLinejoin="round"
      />
      <path
        d="M16 11.15v8.35"
        stroke="currentColor"
        strokeWidth="2.15"
        strokeLinecap="round"
      />
      <circle cx="16" cy="23.35" r="1.25" fill="currentColor" />
    </svg>
  );
}

function ExclamationMarkIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 8 16"
      fill="none"
      focusable="false"
      aria-hidden="true"
    >
      <path
        d="M4 1.5v8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="4" cy="13" r="1.2" fill="currentColor" />
    </svg>
  );
}

const SCAN_LOGS = [
  ['0df29e', 'bad', 'Synchronous remote object proxy returned error'],
  ['c12853', 'good', '[C2 guzzoni.apple.com:443 tcp, traffic class: 600]'],
  ['71b4f', 'bad', '__nwlog_err_simulate_crash already simulated'],
  ['e04d22', 'good', 'nw_connection_get_mptcpinfo_for_connected_endpoint'],
  ['54ef89', 'virus', 'nwlog error: suspicious runtime signature'],
  ['2ad4fc', 'good', 'nw_connection_stats report completed'],
  ['9fd013', 'malware', 'getRegistrationResult() returned unknown client'],
  ['b53e7a', 'good', 'nw_connection endpoint validation complete'],
  ['4ca8d1', 'good', 'network statistics report initialized'],
  ['f7d94b', 'spy tool', 'Background sensor access request detected'],
  ['87c231', 'good', 'registration result verified'],
  ['3f02dd', 'malware', 'Untrusted background process signature'],
  ['a512c0', 'good', 'Secure transport session established'],
  ['d7a513', 'virus', 'Root transaction returned altered response'],
  ['0c9be8', 'good', 'Device orientation state validated'],
  ['b4f27d', 'good', 'Workspace transaction completed'],
  ['6a35ca', 'spy tool', 'Connected endpoint inspection failed'],
  ['ef8b21', 'good', 'Process assertion deactivated'],
  ['39cb04', 'malware', 'Unexpected WebKit storage mutation'],
  ['93a6df', 'good', 'Secure enclave response received'],
  ['c8f1b2', 'virus', 'Remote configuration profile discovered'],
  ['15de7c', 'good', 'Certificate chain inspection completed'],
  ['e29d71', 'bad', 'Unsafe redirect policy enabled'],
  ['7f4c18', 'good', 'Keychain access policy verified'],
  ['9130aa', 'malware', 'Unknown network extension detected'],
  ['c3352e', 'good', 'Privacy manifest loaded'],
  ['ab710d', 'virus', 'Suspicious DNS proxy response'],
  ['d0f310', 'spy tool', 'Clipboard monitor running in background'],
  ['f1129a', 'malware', 'Sensitive data exposure signature matched'],
] as const;

const SCAN_CATEGORIES = [
  ['System Activity', 5],
  ['Security Threat', 10],
  ['Malware Infection', 15],
  ['Unsecured Wi-Fi Connection', 21],
  ['Sensitive Data Exposure', 27],
] as const;

const pageEnter = keyframes`
  from { opacity: 0; transform: translate3d(0, 14px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const stageEnterFromRight = keyframes`
  from {
    opacity: 0.98;
    transform: translate3d(100%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const stageExitToLeft = keyframes`
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  to {
    opacity: 0.92;
    transform: translate3d(-32%, 0, 0);
  }
`;

const stageFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const stageFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const rowEnter = keyframes`
  from { opacity: 0; transform: translate3d(0, -4px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const statusEnter = keyframes`
  0% { opacity: 0.42; transform: scale(0.72); }
  68% { opacity: 1; transform: scale(1.12); }
  100% { opacity: 1; transform: scale(1); }
`;

const dialogEnter = keyframes`
  0% { opacity: 0; transform: translate3d(0, 44px, 0) scale(0.88); }
  72% { opacity: 1; transform: translate3d(0, -5px, 0) scale(1.015); }
  100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const checkoutColumnEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(28px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const checkoutAttentionFirst = keyframes`
  0%, 100% { opacity: 0; }
  38% { opacity: 1; }
`;

const checkoutAttentionSecond = keyframes`
  0%, 100% { opacity: 0; }
  39% { opacity: 1; }
`;

const Page = styled.div`
  min-height: 100vh;
  min-height: 100svh;
  background: #d2d2d2;
  color: #11111f;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text',
    'Helvetica Neue', Arial, sans-serif;
`;

const PhonePage = styled.main`
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100svh;
  margin: 0 auto;
  padding:
    var(--app-safe-area-top)
    var(--app-safe-area-right)
    var(--app-safe-area-bottom)
    var(--app-safe-area-left);
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 0 42px rgba(0, 0, 0, 0.08);

  @media (min-width: 620px) {
    max-width: 430px;
  }
`;

const ScanScreen = styled.section<{ $animateInitial?: boolean }>`
  display: flex;
  min-height: var(--app-safe-area-viewport-height);
  flex-direction: column;
  animation: ${({ $animateInitial }) => ($animateInitial ? pageEnter : 'none')} 320ms ease-out both;
`;

const ExitingScanScreen = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  height: var(--app-safe-area-viewport-height);
  overflow: hidden;
  background: #ffffff;
  pointer-events: none;
  will-change: transform, opacity;
  animation: ${stageExitToLeft} ${STAGE_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1) both;

  @media (prefers-reduced-motion: reduce) {
    will-change: auto;
    animation: ${stageFadeOut} 120ms ease-out both;
  }
`;

const LogTable = styled.div`
  display: flex;
  height: clamp(230px, 36svh, 320px);
  flex-direction: column;
  overflow: hidden;
  border-bottom: 1px solid #d8d8dc;
  background: #f3f3f3;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 10px;
  line-height: 1.15;
`;

const LogHeader = styled.div`
  display: grid;
  grid-template-columns: 74px 86px minmax(260px, 1fr);
  min-width: 520px;
  padding: 7px 10px;
  border-bottom: 1px solid #c7c7cc;
  background: rgba(255, 255, 255, 0.9);
  color: #202022;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
  font-size: 11px;
  font-weight: 600;
`;

const LogTableBody = styled.div`
  display: grid;
  min-height: 0;
  flex: 1 1 auto;
  grid-template-rows: repeat(10, minmax(0, 1fr));
  overflow: hidden;
`;

const LogRow = styled.div<{ $animate: boolean; $desktopOnly?: boolean }>`
  display: ${({ $desktopOnly }) => ($desktopOnly ? 'none' : 'grid')};
  min-height: 0;
  grid-template-columns: 74px 86px minmax(260px, 1fr);
  align-items: center;
  min-width: 520px;
  padding: 0 10px;
  color: #171719;
  animation: ${({ $animate }) => ($animate ? rowEnter : 'none')} 140ms ease-out both;

  &:nth-of-type(odd) {
    background: rgba(255, 255, 255, 0.66);
  }

  @media (min-width: 768px) {
    display: grid;
  }
`;

const Process = styled.span<{ $danger: boolean }>`
  color: ${({ $danger }) => ($danger ? '#eb343b' : '#202022')};
  font-weight: ${({ $danger }) => ($danger ? 700 : 500)};
`;

const ScanContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 30px 26px 24px;
`;

const DetectedBadge = styled.div`
  align-self: center;
  box-sizing: border-box;
  width: 220px;
  max-width: 100%;
  padding: 13px 18px;
  border-radius: 10px;
  background: #ff4249;
  box-shadow: 0 8px 18px rgba(255, 59, 48, 0.19);
  color: #ffffff;
  text-align: center;
  font-size: 20px;
  line-height: 1.1;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ScanTitle = styled.h1`
  margin: 24px 0 20px;
  color: #24243e;
  text-align: center;
  font-size: 23px;
  line-height: 1.2;
  font-weight: 750;
`;

const CategoryList = styled.div`
  display: grid;
  gap: 14px;
`;

const CategoryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  min-height: 25px;
  color: #77776e;
  font-size: 15px;
  line-height: 1.25;
`;

const CategoryStatus = styled.span<{ $animate: boolean; $found: boolean }>`
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: ${({ $found }) => ($found ? '#ff4249' : '#e7e7e9')};
  color: #ffffff;
  opacity: ${({ $found }) => ($found ? 1 : 0.42)};
  transform: scale(1);
  animation: ${({ $animate, $found }) => ($animate && $found ? statusEnter : 'none')}
    220ms cubic-bezier(0.32, 0.72, 0, 1) both;
`;

const StatusExclamationIcon = styled(ExclamationMarkIcon)`
  display: block;
  width: 8px;
  height: 14px;
`;

const PendingStatusDot = styled.span`
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
`;

const ScanFooter = styled.div`
  margin-top: auto;
  padding-top: 28px;
`;

const ProgressTrack = styled.div`
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e2e4;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  width: ${({ $progress }) => `${Math.max(0, Math.min(1, $progress)) * 100}%`};
  height: 100%;
  border-radius: inherit;
  background: #5f5f61;
  transition: width 110ms linear;
`;

const ScanHint = styled.p`
  margin: 14px 0 0;
  color: #9999a3;
  text-align: center;
  font-size: 15px;
  line-height: 1.3;
`;

const RiskDialog = styled.dialog`
  width: min(76vw, 360px);
  max-width: none;
  margin: auto;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 22px;
  background: #ffffff;
  color: #28283e;
  box-shadow: 0 24px 72px rgba(0, 0, 0, 0.28);
  animation: ${dialogEnter} 480ms cubic-bezier(0.16, 1, 0.3, 1) both;

  &:focus {
    outline: none;
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.48);
  }
`;

const DialogBody = styled.div`
  padding: 25px 18px 18px;
`;

const AlertHeading = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 14px;
  align-items: center;
`;

const AlertIcon = styled.div`
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(145deg, #ff292f, #ff4a55);
  color: #ffffff;
`;

const AlertWarningIcon = styled(WarningTriangleIcon)`
  display: block;
  width: 32px;
  height: 32px;
`;

const AlertTitle = styled.h2`
  margin: 0;
  color: #2d2d3d;
  font-size: 30px;
  line-height: 1;
  letter-spacing: -0.03em;
`;

const RiskLevel = styled.p`
  margin: 6px 0 0;
  color: #262635;
  font-size: 18px;
  line-height: 1.2;

  strong {
    color: #ff3b43;
    font-weight: 500;
  }
`;

const ResultTitle = styled.h3`
  margin: 28px 0 12px;
  font-size: 23px;
  line-height: 1.2;
`;

const ResultList = styled.div`
  display: grid;
  gap: 13px;
`;

const ResultRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  color: #7c7c86;
  font-size: 15px;
  line-height: 1.25;

`;

const ResultValue = styled.strong<{ $blurred?: boolean }>`
  min-width: 68px;
  color: #ff3943;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  filter: ${({ $blurred }) => ($blurred ? 'blur(5px)' : 'none')};
  opacity: ${({ $blurred }) => ($blurred ? 0.72 : 1)};
`;

const ProtectButton = styled.button`
  width: calc(100% - 36px);
  min-height: 58px;
  margin: 0 18px 18px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(100deg, #83949c 0%, #4a466c 58%, #69607b 100%);
  box-shadow: 0 10px 22px rgba(53, 49, 79, 0.22);
  color: #ffffff;
  font: inherit;
  font-size: 24px;
  line-height: 1;
  font-weight: 650;
  cursor: pointer;

  &:active {
    transform: scale(0.985);
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 122, 255, 0.5);
    outline-offset: 3px;
  }
`;

const CheckoutScreen = styled.section<{ $entering: boolean }>`
  position: relative;
  z-index: 2;
  min-height: var(--app-safe-area-viewport-height);
  padding: 24px 26px 30px;
  background: #ffffff;
  will-change: ${({ $entering }) => ($entering ? 'transform, opacity' : 'auto')};
  animation: ${({ $entering }) => ($entering ? stageEnterFromRight : 'none')}
    ${STAGE_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1) both;

  @media (prefers-reduced-motion: reduce) {
    will-change: auto;
    animation: ${({ $entering }) => ($entering ? stageFadeIn : 'none')} 120ms ease-out both;
  }
`;

const Summary = styled.header`
  text-align: center;
`;

const SettingsIconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
  line-height: 0;
`;

const SummaryTitle = styled.h1`
  max-width: 430px;
  margin: 0 auto;
  color: #151542;
  font-size: 28px;
  line-height: 1.1;
  letter-spacing: -0.04em;
  font-weight: 800;

  strong {
    color: #ff3038;
  }
`;

const SummaryText = styled.p`
  margin: 15px auto 0;
  color: #36363d;
  font-size: 15px;
  line-height: 1.38;
`;

const SummaryPoints = styled.div`
  display: grid;
  gap: 11px;
  margin-top: 20px;
  text-align: left;
`;

const SummaryPoint = styled.div`
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 11px;
  align-items: center;
  color: #27273e;
  font-size: 14px;
  line-height: 1.3;
`;

const PointIcon = styled.span`
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  line-height: 0;
  border-radius: 10px;
  overflow: hidden;
`;

const PointSettingsIcon = styled.img`
  display: block;
  width: 34px;
  height: 34px;
  object-fit: cover;
  object-position: center;
`;

const PointTapIcon = styled.img`
  display: block;
  width: 34px;
  height: 34px;
  object-fit: cover;
  object-position: center;
`;

const OfferText = styled.p`
  margin: 20px 0 13px;
  color: #55555d;
  text-align: center;
  font-size: 14px;
  line-height: 1.35;
`;

const OriginalPrice = styled.del`
  color: #777780;
  text-decoration-color: currentColor;
  text-decoration-thickness: 2px;
`;

const ResolveButton = styled.button`
  width: 100%;
  min-height: 54px;
  border: 0;
  border-radius: 16px;
  background: #ff3038;
  box-shadow:
    0 3px 0 rgba(139, 38, 43, 0.2),
    0 8px 16px rgba(30, 30, 38, 0.09);
  color: #ffffff;
  font: inherit;
  font-size: 20px;
  font-weight: 750;
  cursor: pointer;

  &:disabled {
    background: #b7b7bd;
    box-shadow:
      0 3px 0 rgba(102, 102, 110, 0.14),
      0 7px 14px rgba(30, 30, 38, 0.07);
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow:
      0 2px 0 rgba(139, 38, 43, 0.18),
      0 5px 10px rgba(30, 30, 38, 0.07);
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 122, 255, 0.5);
    outline-offset: 3px;
  }
`;

const CheckoutTitle = styled.h2`
  margin: 44px 0 18px;
  scroll-margin-block-start: 20px;
  color: #1d1d1f;
  text-align: center;
  font-size: 28px;
  line-height: 1.1;
  letter-spacing: -0.035em;
`;

const CheckoutColumn = styled.div`
  width: 100%;
  min-width: 0;
  min-height: calc(100vh + 24px);
  min-height: calc(100dvh + 24px);
  animation: ${checkoutColumnEnter} ${CHECKOUT_REVEAL_MS}ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const CheckoutCard = styled.div`
  overflow: hidden;
  border: 1px solid #d7d7dd;
  border-radius: 18px;
  background: #f5f5f7;
  box-shadow: 0 12px 28px rgba(21, 21, 42, 0.08);
`;

const CheckoutCardFrame = styled.div<{ $attentionSequence: number }>`
  position: relative;
  min-width: 0;
  border-radius: 18px;

  &::after {
    position: absolute;
    z-index: 3;
    inset: -5px;
    border: 3px solid rgba(0, 122, 255, 0.62);
    border-radius: 23px;
    content: '';
    opacity: 0;
    pointer-events: none;
    animation: ${({ $attentionSequence }) => {
      if ($attentionSequence === 0) {
        return 'none';
      }

      return $attentionSequence % 2 === 0
        ? css`${checkoutAttentionSecond} 620ms ease-out both`
        : css`${checkoutAttentionFirst} 620ms ease-out both`;
    }};
  }
`;

const CheckoutContent = styled.div`
  width: 100%;
  min-width: 0;
`;

const CheckoutCardHeader = styled.div`
  display: flex;
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px;
  border-bottom: 1px solid #d2d2d7;
`;

const CardHeading = styled.h3`
  margin: 0;
  color: #12123c;
  font-size: 19px;
  line-height: 1.1;
  font-weight: 500;
`;

const CardBrands = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CardBrand = styled.img`
  display: block;
  width: 38px;
  height: 25px;
  padding: 4px 5px;
  border: 1px solid #e1e1e5;
  border-radius: 5px;
  background: #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  flex: 0 0 auto;
  object-fit: contain;
  object-position: center;
`;

const PaymentForm = styled.form`
  padding: 18px 16px 16px;
`;

const PaymentFields = styled.div``;

const PaymentActions = styled.div``;

const PaymentHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const PaymentHeading = styled.h4`
  margin: 0;
  color: #161632;
  font-size: 17px;
  line-height: 1.2;
`;

const Protected = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #9898a2;
  font-size: 13px;
  font-weight: 600;
`;

const ProtectedIcon = styled.svg`
  display: block;
  width: 13px;
  height: 16px;
  flex: 0 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
`;

const ApplePayArea = styled.div`
  width: 100%;
  height: 44px;
  margin-bottom: 18px;
`;

const ApplePayButton = styled.button`
  display: block;
  width: 100%;
  height: 44px;
  min-height: 44px;
  border: 1px solid #000000;
  border-radius: 9px;
  background: #ffffff;
  cursor: pointer;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-type: plain;
  -apple-pay-button-style: white-outline;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: -4px;
  }
`;

const Divider = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
  margin: 0 0 16px;
  color: #9999a2;
  font-size: 12px;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    height: 1px;
    background: #d9d9de;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 7px;
  color: #34343f;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 650;
`;

const EmailLabel = styled(FieldLabel)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  min-height: 20px;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${IOS_SYSTEM_BLUE_SOFT};
  color: ${IOS_SYSTEM_BLUE};
  font-size: 10px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: 0.025em;
  text-transform: uppercase;
`;

const EmailHint = styled.p`
  margin: 0 0 8px;
  color: #777783;
  font-size: 12px;
  line-height: 1.35;
`;

const EmailInput = styled.input<{ $invalid: boolean }>`
  width: 100%;
  min-height: 50px;
  padding: 12px 14px;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#e5484d' : '#c9c9d0')};
  border-radius: 12px;
  background: ${({ $invalid }) => ($invalid ? '#fffafa' : '#ffffff')};
  color: #17172d;
  font: inherit;
  font-size: 16px;
  line-height: 1.2;
  outline: 0;
  transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;

  &::placeholder {
    color: #9b9ba4;
  }

  &:focus-visible {
    border-color: ${({ $invalid }) => ($invalid ? '#e5484d' : IOS_SYSTEM_BLUE)};
    box-shadow: 0 0 0 3px ${({ $invalid }) => (
      $invalid ? 'rgba(229, 72, 77, 0.15)' : 'rgba(0, 122, 255, 0.18)'
    )};
  }
`;

const EmailError = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 9px 10px;
  border: 1px solid #ffd5d7;
  border-radius: 10px;
  background: #fff4f4;
  color: #a92f36;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 550;
`;

const EmailErrorIcon = styled.span`
  display: grid;
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  place-items: center;
  margin-top: 1px;
  border-radius: 50%;
  background: #e5484d;
  color: #ffffff;
`;

const EmailExclamationIcon = styled(ExclamationMarkIcon)`
  display: block;
  width: 6px;
  height: 11px;
`;

const StripeField = styled.div`
  position: relative;
  min-height: 176px;
`;

const StripeLoading = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.86);
`;

const Spinner = styled.span`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(0, 122, 255, 0.18);
  border-top-color: ${IOS_SYSTEM_BLUE};
  border-radius: 50%;
  animation: ${spin} 760ms linear infinite;
`;

const SubmitButton = styled.button`
  width: 100%;
  min-height: 52px;
  margin-top: 0;
  border: 0;
  border-radius: 13px;
  background: ${IOS_SYSTEM_BLUE};
  color: #ffffff;
  font: inherit;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 750;
  cursor: pointer;

  &:disabled {
    background: ${IOS_SYSTEM_BLUE_DISABLED};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid ${IOS_SYSTEM_BLUE_FOCUS};
    outline-offset: 3px;
  }
`;

const RetryButton = styled.button`
  display: block;
  min-height: 44px;
  margin: 10px auto 0;
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: ${IOS_SYSTEM_BLUE};
  font: inherit;
  font-size: 14px;
  font-weight: 650;
  cursor: pointer;
`;

const PlanState = styled.div`
  display: grid;
  min-height: 180px;
  place-items: center;
  align-content: center;
  gap: 12px;
  padding: 24px 4px;
  color: #66666f;
  text-align: center;
  font-size: 15px;
  line-height: 1.4;
`;

const ReducedMotionStyles = styled.div`
  @media (prefers-reduced-motion: reduce) {
    ${ScanScreen},
    ${LogRow},
    ${RiskDialog},
    ${Spinner},
    ${CheckoutColumn} {
      animation: none;
    }

    ${CheckoutScreen} {
      transition: none;
    }

    ${CheckoutCardFrame}::after {
      animation-duration: 180ms;
    }

    ${ProgressBar} {
      transition-duration: 0.01ms;
    }

    ${CategoryStatus} {
      animation: none;
    }

  }

  @media (min-width: 620px) {
    ${CheckoutScreen} {
      padding-inline: 30px;
    }
  }
`;

const ResponsiveLayout = styled.div<{
  $enabled: boolean;
  $checkoutRevealed: boolean;
}>`
  min-height: inherit;

  ${({ $enabled, $checkoutRevealed }) => $enabled && css`
    ${Page} {
      min-height: 100dvh;
      padding: 0;
      background:
        radial-gradient(circle at 12% 8%, rgba(255, 255, 255, 0.94), transparent 34%),
        linear-gradient(145deg, #e8e9ee 0%, #cfd1d9 100%);
    }

    ${PhonePage} {
      max-width: none;
      min-height: 100dvh;
      background: #ffffff;
      box-shadow: none;
    }

    ${LogTable} {
      height: clamp(220px, 34svh, 320px);
    }

    ${LogHeader},
    ${LogRow} {
      min-width: 0;
      grid-template-columns: minmax(48px, 0.7fr) minmax(62px, 0.85fr) minmax(0, 3fr);
      column-gap: 8px;
    }

    ${LogHeader} > :last-child,
    ${LogRow} > :last-child {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    ${ScanContent} {
      width: 100%;
      max-width: 560px;
      margin-inline: auto;
    }

    ${RiskDialog} {
      width: calc(100% - 32px);
      max-width: 420px;
      max-height: calc(100dvh - 24px);
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    ${CheckoutScreen} {
      width: 100%;
      max-width: 760px;
      margin-inline: auto;
    }

    ${SummaryText} {
      max-width: 620px;
    }

    ${SummaryPoints} {
      max-width: 620px;
      margin-inline: auto;
    }

    ${ApplePayArea},
    ${ApplePayButton} {
      height: 48px;
      min-height: 48px;
    }

    ${RetryButton} {
      min-height: 48px;
    }

    @media (max-width: 359px) {
      ${LogTable} {
        height: clamp(194px, 31svh, 240px);
        font-size: 9px;
      }

      ${LogHeader},
      ${LogRow} {
        grid-template-columns: 45px 56px minmax(0, 1fr);
        column-gap: 5px;
        padding-inline: 7px;
      }

      ${LogHeader} {
        font-size: 10px;
      }

      ${ScanContent} {
        padding: 20px 16px 18px;
      }

      ${DetectedBadge} {
        width: 100%;
        padding-block: 11px;
        font-size: 18px;
      }

      ${ScanTitle} {
        margin-block: 18px 15px;
        font-size: 21px;
      }

      ${CategoryList} {
        gap: 10px;
      }

      ${CategoryRow} {
        font-size: 14px;
      }

      ${ScanFooter} {
        padding-top: 20px;
      }

      ${DialogBody} {
        padding: 21px 15px 16px;
      }

      ${AlertHeading} {
        grid-template-columns: 44px 1fr;
        gap: 11px;
      }

      ${AlertIcon} {
        width: 44px;
        height: 44px;
      }

      ${AlertTitle} {
        font-size: 27px;
      }

      ${RiskLevel} {
        font-size: 16px;
      }

      ${ResultTitle} {
        margin-top: 23px;
        font-size: 21px;
      }

      ${ResultList} {
        gap: 10px;
      }

      ${ResultRow} {
        gap: 8px;
        font-size: 13px;
      }

      ${ResultValue} {
        min-width: 62px;
        font-size: 14px;
      }

      ${ProtectButton} {
        font-size: 21px;
      }

      ${CheckoutScreen} {
        padding: 20px 14px 24px;
      }

      ${SummaryTitle},
      ${CheckoutTitle} {
        font-size: 25px;
      }

      ${CheckoutCardHeader} {
        flex-wrap: wrap;
        padding: 14px;
      }

      ${CardBrand} {
        width: 34px;
        height: 23px;
      }

      ${PaymentForm} {
        padding-inline: 14px;
      }
    }

    @media (min-width: 768px) {
      ${Page} {
        padding: 20px;
      }

      ${PhonePage} {
        min-height: calc(100dvh - 40px);
        overflow: hidden;
        border-radius: 24px;
        box-shadow: 0 24px 70px rgba(35, 37, 48, 0.18);
      }

      ${ScanScreen} {
        display: grid;
        min-height: calc(100dvh - 40px);
        grid-template-columns: minmax(360px, 1.25fr) minmax(330px, 0.85fr);
      }

      ${LogTable} {
        height: auto;
        min-height: 0;
        border-right: 1px solid #d8d8dc;
        border-bottom: 0;
        font-size: 11px;
      }

      ${LogTableBody} {
        grid-template-rows: repeat(20, minmax(0, 1fr));
      }

      ${LogHeader},
      ${LogRow} {
        padding-inline-start: 24px;
      }

      ${LogHeader} {
        font-size: 12px;
      }

      ${ScanContent} {
        min-width: 0;
        max-width: 600px;
        justify-self: center;
        padding: 36px 34px 30px;
      }

      ${DetectedBadge} {
        width: min(100%, 280px);
      }

      ${ScanTitle} {
        margin-top: 32px;
        font-size: 27px;
      }

      ${CategoryList} {
        gap: 17px;
      }

      ${CategoryRow} {
        font-size: 16px;
      }

      ${CheckoutScreen} {
        min-height: calc(100dvh - 40px);
        padding: 42px 48px 46px;
      }

      ${SummaryTitle} {
        font-size: 32px;
      }

      ${SummaryText},
      ${SummaryPoint},
      ${OfferText} {
        font-size: 16px;
      }

      ${CheckoutTitle} {
        font-size: 32px;
      }

      ${CheckoutCardHeader} {
        padding-inline: 20px;
      }

      ${PaymentForm} {
        padding: 22px 20px 20px;
      }
    }

    @media (min-width: 960px) {
      ${CheckoutScreen} {
        display: grid;
        height: calc(100dvh - 40px);
        min-height: 0;
        max-width: 1220px;
        grid-template-areas:
          'summary checkout';
        grid-template-columns: ${$checkoutRevealed
          ? 'minmax(310px, 0.9fr) minmax(410px, 1.1fr)'
          : 'minmax(310px, 1fr) minmax(0, 0fr)'};
        grid-template-rows: minmax(0, 1fr);
        column-gap: ${$checkoutRevealed ? '54px' : '0'};
        align-content: stretch;
        align-items: start;
        overflow: hidden;
        transition:
          grid-template-columns ${CHECKOUT_REVEAL_MS}ms cubic-bezier(0.16, 1, 0.3, 1),
          column-gap ${CHECKOUT_REVEAL_MS}ms cubic-bezier(0.16, 1, 0.3, 1);
      }

      ${Summary} {
        grid-area: summary;
        align-self: center;
        max-width: 500px;
        justify-self: center;
      }

      ${SummaryPoints} {
        margin-top: 26px;
      }

      ${OfferText} {
        margin-top: 26px;
      }

      ${CheckoutTitle} {
        flex: 0 0 auto;
        margin: 0 0 18px;
      }

      ${CheckoutColumn} {
        position: relative;
        display: grid;
        min-width: 0;
        min-height: 0;
        grid-area: checkout;
        align-self: stretch;
        align-items: safe center;
      }

      ${CheckoutContent} {
        display: flex;
        min-height: 0;
        max-height: 100%;
        flex-direction: column;
        align-self: safe center;
      }

      ${CheckoutCardFrame} {
        display: flex;
        min-height: 0;
        flex: 0 1 auto;
        flex-direction: column;
      }

      ${CheckoutCard} {
        display: flex;
        min-height: 0;
        flex: 1 1 auto;
        flex-direction: column;
      }

      ${CheckoutCardHeader} {
        flex: 0 0 auto;
      }

      ${PaymentForm} {
        display: flex;
        min-height: 0;
        flex: 1 1 auto;
        flex-direction: column;
        overflow: hidden;
      }

      ${PaymentHeadingRow},
      ${ApplePayArea},
      ${Divider} {
        flex: 0 0 auto;
      }

      ${PaymentFields} {
        min-width: 0;
        min-height: 0;
        flex: 1 1 auto;
        margin-inline: -4px;
        padding-inline: 4px;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      ${PaymentActions} {
        flex: 0 0 auto;
      }
    }

    @media (min-width: 1280px) {
      ${Page} {
        padding: 28px;
      }

      ${PhonePage},
      ${ScanScreen},
      ${CheckoutScreen} {
        min-height: calc(100dvh - 56px);
      }

      ${CheckoutScreen} {
        height: calc(100dvh - 56px);
      }

      ${PhonePage} {
        max-width: 1500px;
      }

      ${ScanScreen} {
        grid-template-columns: minmax(0, 1.45fr) minmax(410px, 0.75fr);
      }

      ${ScanContent} {
        padding-inline: 54px;
      }

      ${CheckoutScreen} {
        column-gap: ${$checkoutRevealed ? '76px' : '0'};
        padding-inline: 64px;
      }
    }
  `}
`;

interface ScanResultDialogProps {
  idPrefix: string;
  onProtect: () => void;
}

function ScanResultDialog({ idPrefix, onProtect }: ScanResultDialogProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
      dialog.focus({ preventScroll: true });
    }
  }, []);

  const protectDevice = () => {
    dialogRef.current?.close();
    onProtect();
  };

  return (
    <RiskDialog
      ref={dialogRef}
      tabIndex={-1}
      aria-labelledby={`${idPrefix}-risk-title`}
      aria-describedby={`${idPrefix}-risk-level`}
      onCancel={(event) => event.preventDefault()}
    >
      <DialogBody>
        <AlertHeading>
          <AlertIcon aria-hidden="true">
            <AlertWarningIcon />
          </AlertIcon>
          <div>
            <AlertTitle id={`${idPrefix}-risk-title`}>Attention!</AlertTitle>
            <RiskLevel id={`${idPrefix}-risk-level`}>
              Your Risk Level:
              {' '}
              <strong>High</strong>
            </RiskLevel>
          </div>
        </AlertHeading>
        <ResultTitle>Scan Results:</ResultTitle>
        <ResultList>
          <ResultRow>
            <span>System Activity</span>
            <ResultValue>At Risk</ResultValue>
          </ResultRow>
          <ResultRow>
            <span>Security Threat</span>
            <ResultValue>Detected</ResultValue>
          </ResultRow>
          <ResultRow>
            <span>Malware Infection</span>
            <ResultValue $blurred>Detected</ResultValue>
          </ResultRow>
          <ResultRow>
            <span>Unsecured Wi-Fi Connection</span>
            <ResultValue $blurred>At Risk</ResultValue>
          </ResultRow>
          <ResultRow>
            <span>Sensitive Data Exposure</span>
            <ResultValue $blurred>Detected</ResultValue>
          </ResultRow>
        </ResultList>
      </DialogBody>
      <ProtectButton type="button" onClick={protectDevice}>
        Protect Device
      </ProtectButton>
    </RiskDialog>
  );
}

function PaywallV8Inner({
  layout = 'phone',
  version = 'v8',
}: PaywallV8Props) {
  const [searchParams] = useSearchParams();
  const responsive = layout === 'responsive';
  const checkoutId = `${version}-checkout`;
  const checkoutPanelId = `${checkoutId}-panel`;
  const paymentEmailId = `${version}-payment-email`;
  const paymentEmailHintId = `${paymentEmailId}-hint`;
  const paymentEmailErrorId = `${paymentEmailId}-error`;
  const isPaymentReturn = searchParams.has('payment_intent_client_secret');
  const [stage, setStage] = useState<V8Stage>(() => (isPaymentReturn ? 'checkout' : 'scan'));
  const [foundIssues, setFoundIssues] = useState(0);
  const [checkoutTransitioning, setCheckoutTransitioning] = useState(false);
  const [checkoutRevealed, setCheckoutRevealed] = useState(isPaymentReturn);
  const [checkoutAttentionSequence, setCheckoutAttentionSequence] = useState(0);
  const [plansReady, setPlansReady] = useState(false);
  const [planRetryKey, setPlanRetryKey] = useState(0);
  const [paymentElementContainer, setPaymentElementContainer] = useState<HTMLDivElement | null>(null);
  const checkoutTitleRef = useRef<HTMLHeadingElement | null>(null);
  const stripeFieldRef = useRef<HTMLDivElement | null>(null);
  const checkoutTransitionTimerRef = useRef<number | null>(null);
  const initialAttEmail = resolveAttributionEmail(
    { att_email: searchParams.get('att_email') ?? '' },
    getStoredAttParams()?.att_email,
  ) ?? '';
  const [email, setEmail] = useState(initialAttEmail);
  const [emailTouched, setEmailTouched] = useState(false);

  const weeklyTrialPlan = subscriptionStore.trialPlans.find(
    (plan) => plan.billingFrequency.toLowerCase() === 'weekly',
  ) ?? null;

  useEffect(() => {
    let cancelled = false;

    const loadPlans = async () => {
      try {
        await subscriptionStore.fetchPlans();
        const weeklyPlan = subscriptionStore.trialPlans.find(
          (plan) => plan.billingFrequency.toLowerCase() === 'weekly',
        );
        if (weeklyPlan) {
          subscriptionStore.setSelectedTrialPlan(weeklyPlan.planId);
        }
      }
      finally {
        if (!cancelled) {
          syncPaywallMetaFromUrl(searchParams);
          setPlansReady(true);
        }
      }
    };

    void loadPlans();
    return () => {
      cancelled = true;
    };
  }, [planRetryKey, searchParams]);

  useEffect(() => {
    if (isPaymentReturn) {
      return undefined;
    }

    let animationFrame = 0;
    let resultTimer = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 650 : SCAN_DURATION_MS;
    const startTime = window.performance.now();

    const updateScan = (time: number) => {
      const progress = Math.min(Math.max((time - startTime) / duration, 0), 1);
      setFoundIssues(Math.min(TOTAL_DETECTED_ISSUES, Math.floor(progress * TOTAL_DETECTED_ISSUES)));

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(updateScan);
        return;
      }

      setFoundIssues(TOTAL_DETECTED_ISSUES);
      resultTimer = window.setTimeout(() => setStage('result'), RESULT_DELAY_MS);
    };

    animationFrame = window.requestAnimationFrame(updateScan);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(resultTimer);
    };
  }, [isPaymentReturn]);

  useEffect(() => {
    return () => {
      if (checkoutTransitionTimerRef.current !== null) {
        window.clearTimeout(checkoutTransitionTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!checkoutRevealed || checkoutAttentionSequence === 0) {
      return;
    }

    const checkoutNeedsScroll = !responsive
      || window.matchMedia('(max-width: 959px)').matches;
    if (!checkoutNeedsScroll) {
      return;
    }

    checkoutTitleRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'start',
      inline: 'nearest',
    });
  }, [checkoutAttentionSequence, checkoutRevealed, responsive]);

  const revealCardFields = useCallback(() => {
    const stripeField = stripeFieldRef.current;
    if (!stripeField) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!stripeField.isConnected) {
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      stripeField.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    });
  }, []);

  const {
    initializing,
    processing,
    cardComplete,
    cardReady,
    applePayAvailable,
    error,
    recovering,
    startApplePay,
    submitCard,
    retryInitialization,
    clearError,
  } = useWebTrialCheckout({
    enabled: stage === 'checkout' && checkoutRevealed,
    plan: weeklyTrialPlan,
    paymentElementContainer,
    onPaymentElementFocus: revealCardFields,
  });

  const visibleLogEnd = Math.min(Math.max(foundIssues, 0), SCAN_LOGS.length);
  const visibleLogStart = Math.max(0, visibleLogEnd - 10);
  const desktopVisibleLogStart = Math.max(0, visibleLogEnd - 20);
  const visibleLogs = SCAN_LOGS.slice(visibleLogStart, visibleLogEnd);
  const displayedLogs = SCAN_LOGS.slice(
    responsive ? desktopVisibleLogStart : visibleLogStart,
    visibleLogEnd,
  );
  const desktopOnlyLogCount = Math.max(0, displayedLogs.length - visibleLogs.length);
  const scanProgress = foundIssues / TOTAL_DETECTED_ISSUES;

  const emailIsValid = isValidEmailAddress(email);
  const showEmailError = emailTouched && !emailIsValid;
  const emailErrorMessage = email.trim()
    ? 'Enter a valid email address, for example name@example.com.'
    : 'Enter your email address to continue.';

  const resetEmailValidation = () => {
    setEmailTouched(false);
    clearError();
  };

  const onApplePayClick = () => {
    resetEmailValidation();
    void startApplePay(email).catch(() => undefined);
  };

  const onRetryPaymentInitialization = () => {
    resetEmailValidation();
    retryInitialization();
  };

  const onCardSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!emailIsValid) {
      setEmailTouched(true);
      return;
    }
    void submitCard(email);
  };

  const onResolveClick = () => {
    setCheckoutAttentionSequence((current) => current + 1);
    if (!checkoutRevealed) {
      setCheckoutRevealed(true);
    }
  };

  const openCheckout = () => {
    if (stage === 'checkout') {
      return;
    }

    if (checkoutTransitionTimerRef.current !== null) {
      window.clearTimeout(checkoutTransitionTimerRef.current);
    }

    setCheckoutTransitioning(true);
    setStage('checkout');
    checkoutTransitionTimerRef.current = window.setTimeout(() => {
      setCheckoutTransitioning(false);
      checkoutTransitionTimerRef.current = null;
    }, STAGE_TRANSITION_MS);
  };

  const renderScanScreen = (showResult: boolean, animateInitial: boolean) => (
    <ScanScreen $animateInitial={animateInitial} aria-label="Device security scan">
      <LogTable aria-hidden="true">
        <LogHeader>
          <span>Code</span>
          <span>Process</span>
          <span>Message</span>
        </LogHeader>
        <LogTableBody>
          {displayedLogs.map(([code, process, message], index) => (
            <LogRow
              key={code}
              $animate={animateInitial}
              $desktopOnly={responsive && index < desktopOnlyLogCount}
            >
              <span>{code}</span>
              <Process $danger={process !== 'good'}>{process}</Process>
              <span>{message}</span>
            </LogRow>
          ))}
        </LogTableBody>
      </LogTable>
      <ScanContent>
        <DetectedBadge>
          Detected issues:
          {' '}
          {foundIssues}
        </DetectedBadge>
        <VisuallyHidden aria-live="polite">
          {foundIssues === TOTAL_DETECTED_ISSUES
            ? `${TOTAL_DETECTED_ISSUES} issues detected. Scan complete.`
            : ''}
        </VisuallyHidden>
        <ScanTitle>Scanning For…</ScanTitle>
        <CategoryList>
          {SCAN_CATEGORIES.map(([label, threshold]) => {
            const found = foundIssues >= threshold;
            return (
              <CategoryRow key={label}>
                <span>{label}</span>
                <CategoryStatus
                  $animate={animateInitial}
                  $found={found}
                  aria-label={found ? 'Issue detected' : 'Checking'}
                >
                  {found ? <StatusExclamationIcon /> : <PendingStatusDot />}
                </CategoryStatus>
              </CategoryRow>
            );
          })}
        </CategoryList>
        <ScanFooter>
          <ProgressTrack
            role="progressbar"
            aria-label="Device check progress"
            aria-valuemin={0}
            aria-valuemax={TOTAL_DETECTED_ISSUES}
            aria-valuenow={foundIssues}
          >
            <ProgressBar $progress={scanProgress} />
          </ProgressTrack>
          <ScanHint>
            {foundIssues === TOTAL_DETECTED_ISSUES
              ? 'Scan complete. Preparing results…'
              : 'Hold on! Device check in progress.'}
          </ScanHint>
        </ScanFooter>
      </ScanContent>
      {showResult && <ScanResultDialog idPrefix={version} onProtect={openCheckout} />}
    </ScanScreen>
  );

  return (
    <ResponsiveLayout
      $enabled={responsive}
      $checkoutRevealed={checkoutRevealed}
    >
      <ReducedMotionStyles>
        <Page>
          <PhonePage>
            {stage === 'checkout' && checkoutTransitioning && (
              <ExitingScanScreen aria-hidden="true">
                {renderScanScreen(false, false)}
              </ExitingScanScreen>
            )}
            {stage !== 'checkout'
              ? renderScanScreen(stage === 'result', true)
              : (
                  <CheckoutScreen $entering={checkoutTransitioning}>
                    <Summary>
                      <SettingsIconWrap aria-hidden="true">
                        <IosSettingsIcon
                          badge
                          badgeLabel={TOTAL_DETECTED_ISSUES}
                          size="md"
                          trimTransparentGutter
                        />
                      </SettingsIconWrap>
                      <SummaryTitle>
                        29 System
                        {' '}
                        <strong>Issues</strong>
                        {' '}
                        {responsive ? 'Detected On Your Device' : 'Detected On Your iPhone'}
                      </SummaryTitle>
                      <SummaryText>
                        These issues may expose your device to security threats, unsafe network
                        connections, and potential data risks if not addressed.
                      </SummaryText>
                      <SummaryPoints>
                        <SummaryPoint>
                          <PointIcon aria-hidden="true">
                            <PointSettingsIcon src={settingsReferenceIconUrl} alt="" />
                          </PointIcon>
                          <span>Run a system diagnostic and resolve critical issues.</span>
                        </SummaryPoint>
                        <SummaryPoint>
                          <PointIcon aria-hidden="true">
                            <PointTapIcon src={handTapIconUrl} alt="" />
                          </PointIcon>
                          <span>Choose a secure payment method below to activate protection.</span>
                        </SummaryPoint>
                      </SummaryPoints>
                      <OfferText>
                        {weeklyTrialPlan
                          ? (
                              <>
                                Just
                                {' '}
                                <OriginalPrice>$19.99</OriginalPrice>
                                {' '}
                                $4.99/week.
                                {' '}
                                Cancel anytime.
                              </>
                            )
                          : plansReady
                            ? 'Subscription options are temporarily unavailable.'
                            : 'Loading subscription options…'}
                      </OfferText>
                      <ResolveButton
                        type="button"
                        aria-controls={checkoutPanelId}
                        aria-expanded={checkoutRevealed}
                        disabled={!weeklyTrialPlan || processing}
                        onClick={onResolveClick}
                      >
                        Resolve Now
                      </ResolveButton>
                    </Summary>

                    {checkoutRevealed && (
                      <CheckoutColumn
                        id={checkoutPanelId}
                        role="region"
                        aria-labelledby={checkoutId}
                      >
                        <CheckoutContent>
                          <CheckoutTitle ref={checkoutTitleRef} id={checkoutId}>
                            {isPaymentReturn ? 'Completing Payment' : 'Payment Checkout'}
                          </CheckoutTitle>
                          <CheckoutCardFrame $attentionSequence={checkoutAttentionSequence}>
                            <CheckoutCard>
                              <CheckoutCardHeader>
                                <CardHeading>Credit Card</CardHeading>
                                <CardBrands
                                  role="img"
                                  aria-label="Accepted card networks: Visa, Mastercard, American Express and Discover"
                                >
                                  <CardBrand src={visaLogoUrl} alt="" aria-hidden="true" />
                                  <CardBrand src={mastercardLogoUrl} alt="" aria-hidden="true" />
                                  <CardBrand src={amexLogoUrl} alt="" aria-hidden="true" />
                                  <CardBrand src={discoverLogoUrl} alt="" aria-hidden="true" />
                                </CardBrands>
                              </CheckoutCardHeader>
                              <PaymentForm onSubmit={onCardSubmit} noValidate>
                                <PaymentHeadingRow>
                                  <PaymentHeading>Payment Information</PaymentHeading>
                                  <Protected>
                                    <ProtectedIcon viewBox="0 0 20 24" aria-hidden="true">
                                      <rect x="4.5" y="10" width="11" height="9" rx="1.75" />
                                      <path d="M7 10V7.5a3 3 0 0 1 6 0V10" />
                                    </ProtectedIcon>
                                    Protected
                                  </Protected>
                                </PaymentHeadingRow>

                                {isPaymentReturn
                                  ? (
                                      <PlanState role="status" aria-live="polite">
                                        {recovering && <Spinner aria-hidden="true" />}
                                        <span>
                                          {recovering
                                            ? 'Verifying your payment and activating protection…'
                                            : 'Payment verification needs your attention.'}
                                        </span>
                                        {!recovering && <PaymentError error={error} />}
                                      </PlanState>
                                    )
                                  : !weeklyTrialPlan
                                      ? (
                                          <PlanState>
                                            {!plansReady && <Spinner aria-hidden="true" />}
                                            <span>
                                              {plansReady
                                                ? 'Secure payment options could not be loaded.'
                                                : 'Loading secure payment options…'}
                                            </span>
                                            {plansReady && (
                                              <RetryButton
                                                type="button"
                                                onClick={() => {
                                                  setPlansReady(false);
                                                  setPlanRetryKey((current) => current + 1);
                                                }}
                                              >
                                                Try again
                                              </RetryButton>
                                            )}
                                          </PlanState>
                                        )
                                      : (
                                          <>
                                            {applePayAvailable && (
                                              <ApplePayArea>
                                                <ApplePayButton
                                                  type="button"
                                                  aria-label="Pay with Apple Pay"
                                                  disabled={processing}
                                                  onClick={onApplePayClick}
                                                />
                                              </ApplePayArea>
                                            )}
                                            {applePayAvailable && <Divider>OR</Divider>}

                                            <PaymentFields>
                                              <FieldGroup>
                                                <EmailLabel htmlFor={paymentEmailId}>
                                                  <span>Email address</span>
                                                  <RequiredBadge>Required</RequiredBadge>
                                                </EmailLabel>
                                                <EmailHint id={paymentEmailHintId}>
                                                  Used for your receipt and subscription access.
                                                </EmailHint>
                                                <EmailInput
                                                  id={paymentEmailId}
                                                  name="email"
                                                  type="email"
                                                  value={email}
                                                  placeholder="you@example.com"
                                                  autoComplete="email"
                                                  autoCapitalize="none"
                                                  autoCorrect="off"
                                                  enterKeyHint="next"
                                                  maxLength={254}
                                                  pattern="[^\\s@]+@[^\\s@]+\\.[^\\s@]+"
                                                  required
                                                  spellCheck={false}
                                                  disabled={processing}
                                                  $invalid={showEmailError}
                                                  aria-describedby={showEmailError
                                                    ? `${paymentEmailHintId} ${paymentEmailErrorId}`
                                                    : paymentEmailHintId}
                                                  aria-errormessage={showEmailError
                                                    ? paymentEmailErrorId
                                                    : undefined}
                                                  aria-invalid={showEmailError}
                                                  onBlur={() => setEmailTouched(true)}
                                                  onFocus={resetEmailValidation}
                                                  onChange={(event) => {
                                                    setEmail(event.target.value);
                                                    resetEmailValidation();
                                                  }}
                                                />
                                                {showEmailError && (
                                                  <EmailError
                                                    id={paymentEmailErrorId}
                                                    role="alert"
                                                    aria-live="polite"
                                                  >
                                                    <EmailErrorIcon aria-hidden="true">
                                                      <EmailExclamationIcon />
                                                    </EmailErrorIcon>
                                                    <span>{emailErrorMessage}</span>
                                                  </EmailError>
                                                )}
                                              </FieldGroup>

                                              <FieldGroup>
                                                <FieldLabel>Card details</FieldLabel>
                                                <StripeField ref={stripeFieldRef}>
                                                  {(!cardReady || initializing) && (
                                                    <StripeLoading aria-label="Loading secure card form">
                                                      <Spinner aria-hidden="true" />
                                                    </StripeLoading>
                                                  )}
                                                  <div ref={setPaymentElementContainer} />
                                                </StripeField>
                                              </FieldGroup>
                                            </PaymentFields>

                                            <PaymentActions>
                                              <SubmitButton
                                                type="submit"
                                                disabled={
                                                  processing
                                                  || initializing
                                                  || !cardReady
                                                  || !cardComplete
                                                  || !emailIsValid
                                                }
                                              >
                                                {processing ? 'Processing…' : 'Continue'}
                                              </SubmitButton>

                                              <div role="alert" aria-live="assertive">
                                                <PaymentError error={error} />
                                              </div>
                                              {error && !initializing && !cardReady && (
                                                <RetryButton
                                                  type="button"
                                                  onClick={onRetryPaymentInitialization}
                                                >
                                                  Reload payment form
                                                </RetryButton>
                                              )}
                                            </PaymentActions>
                                          </>
                                        )}
                              </PaymentForm>
                            </CheckoutCard>
                          </CheckoutCardFrame>
                        </CheckoutContent>
                      </CheckoutColumn>
                    )}
                  </CheckoutScreen>
                )}
          </PhonePage>
        </Page>
      </ReducedMotionStyles>
    </ResponsiveLayout>
  );
}

export const PaywallV8 = observer(PaywallV8Inner);
